import React, { useEffect, useState } from "react";

import BottomSheet from "../BottomSheet";
import Loader from "src/components/common/Loader";
import Radio from "src/components/common/Radio";
import Dropdown from "src/components/common/Dropdown";
import Input from "../Input";
import Button from "../Button";

import { User } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { RootState } from "src/redux/store";
import {
  addPatientRequest,
  getPatientListRequest,
} from "src/redux/slices/patientSlice";
import { AddressListResponse } from "src/types/location.types";
import { PatientMemberDetails } from "src/types/patient";
import { EMAIL_REGEX } from "src/constants/regex";
import { FormFieldLabel } from "../FormField";
import { genderOptions } from "src/constants/common";

interface AddPatientBottomSheetProps {
  showBottomSheet: boolean;
  setShowBottomSheet: (show: boolean) => void;
  address: AddressListResponse;
  setPatientModal: (show: boolean) => void;
}

const relationOptions = [
  { value: "Father", label: "Father" },
  { value: "Mother", label: "Mother" },
  { value: "Brother", label: "Brother" },
  { value: "Sister", label: "Sister" },
  { value: "Spouse", label: "Spouse" },
  { value: "Self", label: "Self" },
];

const formFields = {
  relation: {
    name: "relation",
    label: "Relation",
    type: "dropdown",
    options: relationOptions,
    placeholder: "Select Relation",
    required: true,
  },
  fullName: {
    name: "fullName",
    label: "Full Name",
    type: "text",
    placeholder: "Enter patient name",
    required: true,
  },
  email: {
    name: "email",
    label: "Email",
    type: "email",
    placeholder: "Email",
    required: true,
  },
  age: {
    name: "age",
    label: "Age",
    type: "number",
    placeholder: "Age",
    required: true,
  },
  gender: {
    name: "gender",
    label: "Gender",
    type: "radio",
    options: genderOptions,
    placeholder: "Select Gender",
    required: true,
  },
};

const AddPatientModalView: React.FC<{
  patientToEdit?: PatientMemberDetails | null;
  setShowAddPatientModal: (show: boolean) => void;
  setPatientModal: (show: boolean) => void;
}> = ({ patientToEdit, setShowAddPatientModal, setPatientModal }) => {
  const [fields, setFields] = useState<{
    relation: string;
    fullName: string;
    email: string;
    age: string;
    gender: string | null;
  }>({
    relation: patientToEdit?.relation || "",
    fullName: patientToEdit?.name || "",
    email: patientToEdit?.email || "",
    age: String(patientToEdit?.age || "") || "",
    gender: patientToEdit?.gender || "",
  });
  const [errors, setErrors] = useState<{
    relation: string;
    fullName: string;
    email: string;
    age: string;
    gender: string;
  }>({
    relation: "",
    fullName: "",
    email: "",
    age: "",
    gender: "",
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value = "" } = e.target;

    switch (name) {
      case formFields.relation.name:
        if (!value.trim()) {
          setErrors({
            ...errors,
            relation: `${formFields.relation.label} is required`,
          });
        } else {
          setErrors({ ...errors, relation: "" });
        }
        break;
      case formFields.fullName.name:
        if (!value.trim()) {
          setErrors({
            ...errors,
            fullName: `${formFields.fullName.label} is required`,
          });
        } else {
          setErrors({ ...errors, fullName: "" });
        }
        break;
      case formFields.email.name:
        if (!value.trim()) {
          setErrors({
            ...errors,
            email: `${formFields.email.label} is required`,
          });
        } else if (!EMAIL_REGEX.test(value)) {
          setErrors({ ...errors, email: `Invalid ${formFields.email.label}` });
        } else if (value.length < 3) {
          setErrors({
            ...errors,
            email: `${formFields.email.label} too short`,
          });
        } else {
          setErrors({ ...errors, email: "" });
        }
        break;
      case formFields.age.name:
        if (!value.trim()) {
          setErrors({ ...errors, age: `${formFields.age.label} is required` });
        } else {
          setErrors({ ...errors, age: "" });
        }
        break;
      case formFields.gender.name:
        if (!value) {
          setErrors({
            ...errors,
            gender: `${formFields.gender.label} is required`,
          });
        } else {
          setErrors({ ...errors, gender: "" });
        }
        break;
      default:
        break;
    }

    setFields({ ...fields, [name]: value });
  };

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = () => {
    const requiredFields = Object.keys(formFields).filter(
      (field) => formFields[field as keyof typeof formFields].required
    );
    const newErrors = { ...errors };
    requiredFields.forEach((field) => {
      if (!fields[field as keyof typeof fields]) {
        newErrors[field as keyof typeof newErrors] = `${
          formFields[field as keyof typeof formFields].label
        } is required`;
      }
    });
    setErrors(newErrors);

    const isValid = Object.values(newErrors).every((error) => error === "");

    if (!isValid) {
      return;
    }

    handleSave();
  };

  const handleSave = () => {
    setIsLoading(true);
    new Promise((resolve, reject) => {
      setIsLoading(true);
      dispatch(
        addPatientRequest({
          resolve,
          reject,
          payload: {
            age: Number(fields.age),
            email: fields.email,
            gender: fields.gender,
            name: fields.fullName,
            relation: fields.relation,
            ...(patientToEdit && {
              member_id: patientToEdit.patient_member_rel_id,
              patient_id: patientToEdit.patient_id,
            }),
          },
        })
      );
    })
      .then(() => {
        setShowAddPatientModal(false);
        navigate(`/app/${supportProgramKey}/book-lab-test/checkout`);
      })
      .catch((err) => {
        console.error("Error adding patient", err);
      })
      .finally(() => {
        setIsLoading(false);
        setPatientModal(false);
      });
  };

  return (
    <div className="flex flex-col p-4">
      {/* Scrollable form content */}
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="space-y-4">
          <Dropdown
            name={formFields.relation.name}
            label={formFields.relation.label}
            options={formFields.relation.options}
            placeholder={formFields.relation.placeholder}
            fullWidth
            onChange={(value: string | number) =>
              handleChange({
                target: {
                  name: formFields.relation.name,
                  value: value as string,
                },
              } as React.ChangeEvent<HTMLInputElement>)
            }
            value={
              fields[formFields.relation.name as keyof typeof fields] || ""
            }
            error={!!errors[formFields.relation.name as keyof typeof errors]}
            helperText={errors[formFields.relation.name as keyof typeof errors]}
            required={formFields.relation.required}
          />

          <Input
            label={formFields.fullName.label}
            name={formFields.fullName.name}
            type="text"
            placeholder={formFields.fullName.placeholder}
            fullWidth
            className="mt-2"
            value={
              fields[formFields.fullName.name as keyof typeof fields] || ""
            }
            onChange={handleChange}
            error={!!errors[formFields.fullName.name as keyof typeof errors]}
            helperText={errors[formFields.fullName.name as keyof typeof errors]}
            required={formFields.fullName.required}
          />

          <Input
            label={formFields.email.label}
            name={formFields.email.name}
            type="email"
            placeholder={formFields.email.placeholder}
            fullWidth
            className="mt-2"
            value={fields[formFields.email.name as keyof typeof fields] || ""}
            onChange={handleChange}
            error={!!errors[formFields.email.name as keyof typeof errors]}
            helperText={errors[formFields.email.name as keyof typeof errors]}
            required={formFields.email.required}
          />
          <p className="text-sm text-gray-500 mt-1">
            User will receive updates of order status and reports on this email
          </p>

          <Input
            label={formFields.age.label}
            name={formFields.age.name}
            type="number"
            placeholder={formFields.age.placeholder}
            fullWidth
            value={fields[formFields.age.name as keyof typeof fields] || ""}
            onChange={handleChange}
            error={!!errors[formFields.age.name as keyof typeof errors]}
            helperText={errors[formFields.age.name as keyof typeof errors]}
            required={formFields.age.required}
          />

          <div>
            <FormFieldLabel
              label={formFields.gender.label}
              required={formFields.gender.required}
            />
            <ul className="flex flex-col gap-4">
              {formFields?.gender?.options?.map((opt) => (
                <li key={opt?.value}>
                  <button
                    onClick={() =>
                      handleChange({
                        target: {
                          name: formFields.gender.name,
                          value: opt?.value,
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    className={`flex items-center gap-2 w-full text-left border rounded-lg p-3 ${
                      fields[formFields.gender.name as keyof typeof fields] ===
                      opt?.value
                        ? "border-[var(--app-primary)] bg-[var(--app-primary-bg)]"
                        : "border-gray-300"
                    }`}
                  >
                    <Radio
                      checked={
                        fields[
                          formFields.gender.name as keyof typeof fields
                        ] === opt?.value
                      }
                      onChange={() =>
                        handleChange({
                          target: {
                            name: formFields.gender.name,
                            value: opt?.value,
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />
                    <p className="font-medium text-sm text-[var(--text-primary)]">
                      {opt?.label}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Fixed Save button at bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-4 z-10 max-w-lg mx-auto">
        <Button
          variant="contained"
          rounded="full"
          fullWidth
          onClick={handleSubmit}
          loading={isLoading}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const AddPatientBottomSheet: React.FC<AddPatientBottomSheetProps> = React.memo(
  ({
    showBottomSheet,
    setShowBottomSheet,
    address,
    setPatientModal,
  }: AddPatientBottomSheetProps) => {
    const params = useParams();
    const supportProgramKey = params["support-program-key"];
    const navigate = useNavigate();
    const [pincode, setPincode] = useState("");
    const [isPincodeLoading, setIsPincodeLoading] = useState(false);
    const dispatch = useAppDispatch();
    const patientListState = useAppSelector(
      (state: RootState) => state.patient.getPatientList
    );
    const patientList = patientListState?.data;
    const [showAddPatientModal, setShowAddPatientModal] =
      useState<boolean>(false);
    const [patientToEdit, setPatientToEdit] =
      useState<PatientMemberDetails | null>(null);

    useEffect(() => {
      getPatientList();
    }, [dispatch]);

    const getPatientList = () => {
      new Promise((resolve, reject) => {
        dispatch(getPatientListRequest({ resolve, reject, payload: {} }));
      });
    };
    return (
      <>
        <BottomSheet
          title="Select Patient"
          show={showBottomSheet}
          handleClose={() => setShowBottomSheet(false)}
          showCloseButton
        >
          <div className="relative w-full p-4">
            <div className="flex justify-end">
              {patientList && patientList?.length > 0 && (
                <p
                  className="text-sm text-green-500"
                  onClick={() => setShowAddPatientModal(true)}
                >
                  + Add New
                </p>
              )}
            </div>
            {/* Location content goes here */}
            <div className="mt-4 space-y-4">
              {/* Address Row */}
              {patientListState?.isLoading ? (
                <div className="flex flex-col justify-center items-center gap-4 h-full">
                  <Loader />
                  <p className="text-sm text-gray-500">Loading patients...</p>
                </div>
              ) : patientListState?.data &&
                (patientListState?.data || [])?.length > 0 ? (
                patientListState?.data?.map((patient: PatientMemberDetails) => (
                  <div
                    onClick={() => {
                      if (
                        !patient.age ||
                        !patient.gender ||
                        !patient.relation ||
                        !patient.name ||
                        !patient.email
                      ) {
                        setPatientToEdit(patient);
                        setShowAddPatientModal(true);
                        return;
                      }
                      navigate(
                        `/app/${supportProgramKey}/book-lab-test/select-time-slot`,
                        {
                          state: {
                            address: address,
                            patient: patient,
                          },
                        }
                      );
                    }}
                  >
                    <div className="flex items-center gap-2 border border-gray-200 rounded-lg p-2 mt-2">
                      <div className="w-10 h-10 bg-[var(--app-primary)] text-white text-xs p-1 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <p className="text-sm text-gray-600 font-semibold">
                          {patient.name}
                        </p>
                        <p className="text-sm text-gray-400">
                          {[
                            (patient?.age || 0) > 0 && `${patient?.age} years`,
                            patient?.gender ? patient?.gender : "",
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                // Else block — when addressList is empty
                <div className="text-sm text-gray-500 text-center py-4">
                  No patient present. Please add a new patient.
                </div>
              )}

              {/* Select Patient Button */}
              {patientList?.length === 0 && (
                <Button size="lg" fullWidth>
                  Add Patient
                </Button>
              )}
            </div>
          </div>
        </BottomSheet>

        <BottomSheet
          title="Add Patient Details"
          show={showAddPatientModal}
          showCloseButton
          handleClose={() => setShowAddPatientModal(false)}
        >
          <AddPatientModalView
            patientToEdit={patientToEdit}
            setShowAddPatientModal={setShowAddPatientModal}
            setPatientModal={setPatientModal}
          />
        </BottomSheet>
      </>
    );
  }
);

export default AddPatientBottomSheet;
